//to fetch email analytics

import { useState, useCallback, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { apiFetch } from "@/lib/api/apiFetch";

type Pagination = {
  page: number;
  limit: number;
  total?: number;
};

type Analytics = any;       
type EmailLog = any;

type CampaignWithStatsRow = {
  campaignName: string;
  sentAt: string | null;
  Delivered: number;
  Open: number;
  Click: number;
  Bounce: number;
};


/**
 * Custom React hook to fetch and manage Email Analytics data.
 *
 * This hook handles:
 * - Fetching analytics summary and email logs from the backend
 * - Filtering by time range and email search string
 * - Pagination state management
 * - Loading and error handling
 * - Manual and automatic re-fetching of data
 *
 * The API request is triggered automatically when:
 * - `timeRange` changes
 * - `searchString` changes
 * - Pagination (`page`, `limit`) changes
 *
 * @param {string} [initialTimeRange="today"]
 * Initial time range filter for analytics data (e.g. "today", "week", "month").
 *
 * @returns {Object} useEmailAnalyticsResult
 *
 * @returns {Analytics | null} useEmailAnalyticsResult.analytics
 * Aggregated email analytics data returned from the API.
 *
 * @returns {EmailLog[]} useEmailAnalyticsResult.emailLogs
 * List of email tracking logs.
 *
 * @returns {Pagination} useEmailAnalyticsResult.pagination
 * Pagination state containing page number, limit, and total records.
 *
 * @returns {string} useEmailAnalyticsResult.timeRange
 * Currently selected time range filter.
 *
 * @returns {string} useEmailAnalyticsResult.searchString
 * Email search input used to filter logs.
 *
 * @returns {boolean} useEmailAnalyticsResult.isLoading
 * Indicates whether the analytics data is currently being fetched.
 *
 * @returns {string | null} useEmailAnalyticsResult.error
 * Error message if the API request fails.
 *
 * @returns {(value: string) => void} useEmailAnalyticsResult.setTimeRange
 * Setter to update the selected time range.
 *
 * @returns {(value: string) => void} useEmailAnalyticsResult.setSearchString
 * Setter to update the email search string.
 *
 * @returns {(pagination: Pagination) => void} useEmailAnalyticsResult.setPagination
 * Setter to update pagination state.
 *
 * @returns {() => Promise<void>} useEmailAnalyticsResult.refetch
 * Manually triggers re-fetching of analytics data.
 * @author uplSouvik
 */

export function useEmailAnalytics(initialTimeRange: string = "today", initialStartDate?: string, initialEndDate?: string) {
  const [timeRange, setTimeRangeState] = useState(initialTimeRange);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [searchString, setSearchString] = useState("");
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [campaignWithStats, setCampaignWithStats] = useState<CampaignWithStatsRow[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isCampaignWithStatsLoading, setIsCampaignWithStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setTimeRange = useCallback((range: string, newStartDate?: string, newEndDate?: string) => {
    setTimeRangeState(range);
    if (newStartDate) setStartDate(newStartDate);
    if (newEndDate) setEndDate(newEndDate);
  }, []);

  // If the UI hasn't provided explicit start/end (e.g. initial "Today" load),
  // derive them from the preset timeRange so the backend can filter properly.
  useEffect(() => {
    const now = new Date();

    const ensurePresetRange = (preset: "today" | "7days" | "30days", subtractDays: number) => {
      // Only auto-fill when missing so custom selections aren't overridden.
      if (startDate && endDate) return;

      const start = new Date(now);
      start.setDate(start.getDate() - subtractDays);
      start.setHours(0, 0, 0, 0);

      setStartDate(start.toISOString());
      setEndDate(now.toISOString());
    };

    if (timeRange === "all") {
      if (startDate) setStartDate(undefined);
      if (endDate) setEndDate(undefined);
      return;
    }

    if (timeRange === "today") {
      ensurePresetRange("today", 0);
      return;
    }

    if (timeRange === "7days") {
      ensurePresetRange("7days", 6);
      return;
    }

    if (timeRange === "30days") {
      ensurePresetRange("30days", 29);
      return;
    }
  }, [timeRange, startDate, endDate]);

  // ---------------------------
  // Main fetch function
  // ---------------------------
  const fetchAnalytics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams({});

      // For Today/7 Days/30 Days/Custom we pass explicit date filters to the API.
      // "All Time" sends no date params (backend interprets as no createdAt filter).
      if (timeRange !== "all") {
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
      }

      if (searchString.trim()) {
        params.set("email", searchString.trim());
      }

      const data = await apiFetch(`/api/email/analytics?${params}`);

      setAnalytics(data.analytics);
      setEmailLogs(data.trackLogs || []);
      setPagination(data.pagination);
    } catch (error) {
      setError("Failed to fetch analytics");

      toast({
        title: "Error",
        description: "Failed to fetch analytics",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [timeRange, startDate, endDate, pagination.page, pagination.limit, searchString]);

  // Auto run on first load + when dependencies change
  // Note: We debounce or rely on specific triggers usually, but here dependencies are explicit
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const fetchCampaignsWithStats = useCallback(async () => {
    try {
      setIsCampaignWithStatsLoading(true);

      const params = new URLSearchParams({
        page: "1",
        limit: "10",
      });

      // For Today/7 Days/30 Days/Custom we pass explicit date filters to the API.
      if (timeRange !== "all") {
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
      }

      // Guard: on initial mount, startDate/endDate may still be undefined.
      if (timeRange !== "all" && (!startDate || !endDate)) return;

      const data = await apiFetch(`/api/campaigns/with-stats?${params}`);
      setCampaignWithStats((data?.data as CampaignWithStatsRow[]) || []);
    } catch (e: any) {
      toast({
        title: "Error",
        description: e?.message || "Failed to fetch campaign stats",
        variant: "destructive",
      });
    } finally {
      setIsCampaignWithStatsLoading(false);
    }
  }, [timeRange, startDate, endDate]);

  useEffect(() => {
    fetchCampaignsWithStats();
  }, [fetchCampaignsWithStats]);

  return {
    analytics,
    emailLogs,
    campaignWithStats,
    pagination,
    timeRange,
    startDate,
    endDate,
    searchString,
    isLoading,
    isCampaignWithStatsLoading: isCampaignWithStatsLoading,
    error,

    // handlers
    setTimeRange,
    setSearchString,
    setPagination,

    // manual refresh
    refetch: fetchAnalytics,
    refetchCampaignsWithStats: fetchCampaignsWithStats,
  };
}
